import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, SelectChangeEvent } from "@mui/material";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { useGetIntegrationLogsService } from "subModule/src/services/activitylogs/useActivityLogs";
import IntegrationLogsSearchHeader from "components/ActivityLogs/IntegrationLogsSearchHeader";
import {
  ActivityLogProfileService,
  CommonProfileService,
} from "subModule/src/core/services";
import { getIntegrationRetryTarget } from "Helpers/integrationLogRetry";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { themes } from "theme/themes";

interface SearchFormData {
  [key: string]: string | any;
}

export default function IntegrationLogsListing() {
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>([
    "module",
    "action",
    "status",
  ]);
  const [search, setSearch] = useState<any>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    module: null,
    action: null,
    status: null,
    vin: "",
    direction: null,
    start: null,
    end: null,
  });
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetIntegrationLogsService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
  });
  const integrationPage = tableData?.pages?.[0];
  const integrationListData =
    integrationPage?.data?.content != null
      ? integrationPage.data
      : integrationPage;
  const integrationRows = integrationListData?.content || [];
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const modules = [
    { label: t("BAYANAT"), value: 1 },
    { label: t("VEHICLE"), value: 2 },
    { label: t("JOBCARD"), value: 3 },
    { label: t("WORKORDER"), value: 4 },
    { label: t("INVOICE"), value: 5 },
    { label: t("ESTIMATE"), value: 6 },
    { label: t("ACCOUNT"), value: 7 },
    { label: t("EINVOICE"), value: 8 },
    { label: t("BILLING"), value: 9 },
  ];
  const actions = [
    { label: t("VALIDATE_VIN"), value: 1 },
    { label: t("AUTH_LOGIN"), value: 2 },
    { label: t("FETCH_VEHICLE_BY_VIN"), value: 3 },
    { label: t("SEND_JOBCARD"), value: 4 },
    { label: t("SEND_WORKORDER"), value: 5 },
    { label: t("SEND_INVOICE"), value: 6 },
    { label: t("CANCEL_JOBCARD"), value: 7 },
    { label: t("CANCEL_WORKORDER"), value: 8 },
    { label: t("SEND_ESTIMATE"), value: 9 },
    { label: t("CREATE_ACCOUNT"), value: 10 },
    { label: t("UPDATE_ACCOUNT"), value: 11 },
    { label: t("ARCHIVE_ACCOUNT"), value: 12 },
    { label: t("ACTIVE_ACCOUNT"), value: 13 },
    { label: t("CREATE_VEHICLE"), value: 14 },
    { label: t("UPDATE_VEHICLE"), value: 15 },
    { label: t("BULK_CREATE_UPDATE_VEHICLE"), value: 16 },
    { label: t("ARCHIVE_VEHICLE"), value: 17 },
    { label: t("SEND_EVENT"), value: 19 },
    { label: t("SEND_EINVOICE"), value: 18 },
  ];
  const statuses = [
    { label: t("SUCCESS"), value: 1 },
    { label: t("FAILED"), value: 2 },
  ];
  const directions = [
    { label: "Sianty", value: "Sianty" },
    { label: t("billingApp"), value: "FlexPrice" },
    { label: t("FleetOS"), value: "FMS" },
    { label: t("BAYANAT"), value: "Bayanat" },
  ];
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          {
            variant: "error",
          },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.start, searchFormData.end]);
  const handleChangePage = (newPageNo: number) => setPageNo(newPageNo + 1);
  const handleChangeRows = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setFilter(["module", "action", "status"]);
    setSearchFormData({
      module: null,
      action: null,
      status: null,
      vin: "",
      direction: null,
      start: null,
      end: null,
    });
    setSearch({});
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    const selectedFilters =
      typeof value === "string" ? value.split(",") : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          selectedFilters.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : null,
        ]),
      );
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.start = prevFormData.start;
        updatedFormData.end = prevFormData.end;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) clearSearch();
  };
  const handleChangeSearch = (field: string, value: any) => {
    if (field === "dateInterval") {
      setSearchFormData((prevData) => ({
        ...prevData,
        start: value[0],
        end: value[1],
      }));
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
      return;
    }
    const val =
      field === "vin"
        ? value
        : field === "direction"
          ? value?.value || null
          : value
            ? parseInt(value.value) || null
            : null;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    const { start, end, ...rest } = searchFormData;
    if (start && end) {
      filteredSearch.start = start;
      filteredSearch.end = end;
    } else {
      setSearchFormData((prev) => ({
        ...prev,
        start: null,
        end: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "")
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
    setLoading(false);
  };
  const DownloadIntegrationLogs = () => {
    CommonProfileService.exportEntityWithParams(
      { ...search },
      "integration/logs/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `IntegrationLogs.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const handleRetryIntegrationLog = (row: any) => {
    const target = getIntegrationRetryTarget(row);
    if (!target || target.kind !== "invoice") return;
    setRetryingId(row.id);
    ActivityLogProfileService.sendInvoiceToIntegration(target.id)
      .then((response: any) => {
        if (response?.success) {
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={t("retry") + " " + t("successfully")}
            />,
            { variant: "success" },
          );
          fetchData();
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={response?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setRetryingId(null));
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        heading={t("integrationLogs")}
        icon={
          <SummarizeOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"create"}
        btnType={"create"}
        isBtnEnabled={false}
        showCreateBtn={false}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            {
              id: "integrationReferenceNo",
              label: "referenceNo",
              numeric: false,
              width: "14%",
            },
            // {
            //   id: "integrationJobId",
            //   label: "jobId",
            //   numeric: false,
            //   width: 120,
            // },
            {
              id: "integrationModule",
              label: "module",
              numeric: false,
              width: "10%",
            },
            {
              id: "integrationAction",
              label: "action",
              numeric: false,
              width: "12%",
            },
            {
              id: "integrationStatus",
              label: "status",
              numeric: false,
              width: "10%",
            },
            {
              id: "integrationVin",
              label: "vin",
              numeric: false,
              width: "14%",
            },
            {
              id: "direction",
              label: "direction",
              numeric: false,
              width: "16%",
            },
            {
              id: "createdAt",
              label: "createdAt",
              numeric: false,
              width: "14%",
            },
            {
              id: "integrationRetry",
              label: "actions",
              numeric: false,
              width: "8%",
            },
          ]}
          data={integrationRows}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={integrationListData || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={false}
          actions={[]}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          rowClick={true}
          loadingSearch={false}
          onIntegrationLogRetry={handleRetryIntegrationLog}
          integrationRetryLoadingId={retryingId}
          headerComponent={
            <IntegrationLogsSearchHeader
              searchFormData={searchFormData}
              handleChangeFilter={handleChangeFilter}
              handleChangeSearch={handleChangeSearch}
              handleSubmitSearch={handleSubmitSearch}
              clearSearch={clearSearch}
              endActions={
                <Button
                  id="import-button"
                  variant="contained"
                  disableElevation
                  onClick={() => {
                    setDownloading(true);
                    DownloadIntegrationLogs();
                  }}
                  className="bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]"
                  sx={{
                    outline: "2px solid",
                    color: themeColors?.primary,
                    borderColor: themeColors?.primary,
                    height: 38,
                    minHeight: 38,
                  }}
                  disabled={downloading}
                >
                  {downloading ? (
                    <CircularProgress
                      sx={{ color: themeColors?.primary, marginRight: "8px" }}
                      size={16}
                    />
                  ) : null}
                  {t("export")}
                </Button>
              }
              {...{
                filter,
                search,
                modules,
                actions,
                statuses,
                directions,
              }}
            />
          }
        />
      )}
    </Box>
  );
}
